/**
 * InvestorPortalLayout
 * - Mobile (<768px): top bar + bottom-nav (5 tap targets)
 * - Desktop (>=768px): left sidebar + topbar
 * Design: porcelain bg, skyMist accents (investor section), white cards.
 */
import { useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  ChevronDown, ClipboardList, Home, LogOut, Mail, MessageSquare,
  MoreHorizontal, Shield, Sparkles, User, Users, BookOpen, Calendar,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useInvestorPortalAuth } from "@/contexts/InvestorPortalAuthContext";

const PRIMARY = [
  { to: "/portal/investor/dashboard", labelKey: "invPortal.nav.dashboard", icon: Home },
  { to: "/portal/investor/profile", labelKey: "invPortal.nav.profile", icon: User },
  { to: "/portal/investor/thesis", labelKey: "invPortal.nav.thesis", icon: BookOpen },
  { to: "/portal/investor/timeline", labelKey: "invPortal.nav.timeline", icon: ClipboardList },
  { to: "/portal/investor/security", labelKey: "invPortal.nav.security", icon: Shield },
  { to: "/portal/investor/referral", labelKey: "invPortal.nav.referral", icon: Users },
];

const SECONDARY = [
  { to: "/portal/investor/matches", labelKey: "invPortal.nav.matches", icon: Sparkles, phaseKey: "phase8" },
  { to: "/portal/investor/messages", labelKey: "invPortal.nav.messages", icon: MessageSquare, phaseKey: "phase10" },
  { to: "/portal/investor/bookings", labelKey: "invPortal.nav.bookings", icon: Calendar, phaseKey: "phase10" },
];

const MOBILE_PRIMARY = [
  { to: "/portal/investor/dashboard", labelKey: "invPortal.nav.dashboard", icon: Home, testId: "inv-bottomnav-home" },
  { to: "/portal/investor/profile", labelKey: "invPortal.nav.profile", icon: User, testId: "inv-bottomnav-profile" },
  { to: "/portal/investor/thesis", labelKey: "invPortal.nav.thesis", icon: BookOpen, testId: "inv-bottomnav-thesis" },
  { to: "/portal/investor/referral", labelKey: "invPortal.nav.referral", icon: Users, testId: "inv-bottomnav-referral" },
];

export default function InvestorPortalLayout() {
  const { t } = useTranslation();
  const { session, logout } = useInvestorPortalAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [profileOpen, setProfileOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  const onLogout = async () => {
    await logout();
    navigate("/portal/investor/login");
  };

  const me = session?.me;
  const firstName = me?.personal?.first_name || "";
  const initial = (firstName || me?.personal?.email || "?").slice(0, 1).toUpperCase();
  const orgName = me?.organization?.name || "";

  return (
    <div className="min-h-screen bg-porcelain text-cocoa flex" data-testid="investor-portal-layout">
      {/* DESKTOP SIDEBAR */}
      <aside
        className="hidden md:flex md:w-64 lg:w-72 md:flex-col md:fixed md:inset-y-0 md:left-0 bg-white border-r border-sandBeige z-30"
        data-testid="inv-portal-sidebar"
      >
        <div className="px-6 py-6 border-b border-sandBeige">
          <p className="font-serif text-2xl text-cocoa lowercase tracking-tight">
            womenintech<span className="text-raspberry">.life</span>
          </p>
          <p className="text-xs text-warmGrey mt-1">{t("invPortal.shell.subtitle")}</p>
        </div>
        <nav className="flex-1 px-3 py-5 overflow-y-auto">
          <p className="px-3 pb-2 text-[10px] uppercase tracking-wider text-warmGrey/80">
            {t("invPortal.nav.sectionMain")}
          </p>
          <ul className="flex flex-col gap-1">
            {PRIMARY.map(({ to, labelKey, icon: Icon }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-2xl text-sm transition-colors ${
                      isActive ? "bg-skyMist text-cocoa font-medium" : "text-warmGrey hover:text-cocoa hover:bg-ivory"
                    }`
                  }
                  data-testid={`inv-sidebar-${to.split("/").pop()}`}
                >
                  <Icon size={16} />
                  {t(labelKey)}
                </NavLink>
              </li>
            ))}
          </ul>
          <p className="mt-5 px-3 pb-2 text-[10px] uppercase tracking-wider text-warmGrey/80">
            {t("invPortal.nav.sectionSoon")}
          </p>
          <ul className="flex flex-col gap-1">
            {SECONDARY.map(({ to, labelKey, icon: Icon, phaseKey }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-2xl text-sm transition-colors ${
                      isActive ? "bg-lavenderFog/70 text-cocoa font-medium" : "text-warmGrey hover:text-cocoa hover:bg-ivory"
                    }`
                  }
                  data-testid={`inv-sidebar-${to.split("/").pop()}`}
                >
                  <Icon size={16} />
                  <span className="flex-1">{t(labelKey)}</span>
                  <span className="text-[10px] uppercase tracking-wide text-warmGrey/70">
                    {t(`invPortal.phases.${phaseKey}`)}
                  </span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <div className="px-3 pb-5 pt-3 border-t border-sandBeige relative">
          <button
            type="button"
            onClick={() => setProfileOpen((v) => !v)}
            className="w-full flex items-center gap-3 p-2 rounded-2xl hover:bg-ivory transition-colors"
            data-testid="inv-sidebar-profile-toggle"
          >
            <div className="w-9 h-9 rounded-full bg-skyMist text-cocoa flex items-center justify-center font-medium text-sm">
              {initial}
            </div>
            <div className="flex-1 text-left min-w-0">
              <p className="text-sm font-medium text-cocoa truncate">{firstName || t("invPortal.shell.investor")}</p>
              <p className="text-xs text-warmGrey truncate">{orgName}</p>
            </div>
            <ChevronDown size={14} className="text-warmGrey" />
          </button>
          {profileOpen && (
            <div className="absolute bottom-16 left-3 right-3 bg-white border border-sandBeige rounded-2xl shadow-soft p-2 z-40">
              <button
                type="button"
                onClick={onLogout}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-cocoa hover:bg-ivory"
                data-testid="inv-sidebar-logout"
              >
                <LogOut size={14} /> {t("invPortal.shell.logout")}
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* MAIN */}
      <div className="flex-1 md:ml-64 lg:ml-72 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 bg-porcelain/95 backdrop-blur border-b border-sandBeige h-16 px-5 md:px-8 flex items-center gap-3">
          <p className="md:hidden font-serif text-xl text-cocoa lowercase tracking-tight">
            womenintech<span className="text-raspberry">.life</span>
          </p>
          <h1 className="hidden md:block font-serif text-xl md:text-2xl text-cocoa truncate" data-testid="inv-portal-topbar-title">
            {t("invPortal.shell.portalTitle")}
          </h1>
          <div className="flex-1" />
          <button
            type="button"
            onClick={onLogout}
            className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm text-warmGrey hover:text-cocoa hover:bg-ivory transition-colors"
            data-testid="inv-topbar-logout"
          >
            <LogOut size={14} /> {t("invPortal.shell.logout")}
          </button>
          <div className="md:hidden w-10 h-10 rounded-full bg-skyMist text-cocoa flex items-center justify-center text-sm font-medium" data-testid="inv-topbar-initial">
            {initial}
          </div>
        </header>

        <motion.main
          key={location.pathname}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.28, ease: "easeOut" }}
          className="flex-1 px-5 md:px-8 py-6 pb-28 md:pb-10"
          data-testid="inv-portal-main"
        >
          <Outlet />
        </motion.main>
      </div>

      {/* MOBILE BOTTOM NAV */}
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 bg-white border-t border-sandBeige z-30"
        style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0px)" }}
        aria-label={t("invPortal.shell.mobileNav")}
        data-testid="inv-portal-bottom-nav"
      >
        <ul className="flex justify-around items-stretch">
          {MOBILE_PRIMARY.map(({ to, labelKey, icon: Icon, testId }) => (
            <li key={to} className="flex-1">
              <NavLink
                to={to}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center gap-1 py-3 min-h-[56px] text-[11px] ${
                    isActive ? "text-raspberry" : "text-warmGrey"
                  }`
                }
                data-testid={testId}
              >
                <Icon size={18} />
                <span>{t(labelKey)}</span>
              </NavLink>
            </li>
          ))}
          <li className="flex-1">
            <button
              type="button"
              onClick={() => setMoreOpen(true)}
              className="w-full flex flex-col items-center justify-center gap-1 py-3 min-h-[56px] text-[11px] text-warmGrey"
              data-testid="inv-bottomnav-more"
            >
              <MoreHorizontal size={18} />
              <span>{t("invPortal.nav.more")}</span>
            </button>
          </li>
        </ul>
      </nav>

      {/* MOBILE "More" SHEET */}
      <AnimatePresence>
        {moreOpen && (
          <motion.div
            className="md:hidden fixed inset-0 z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="inv-portal-more-sheet"
          >
            <div
              className="absolute inset-0 bg-cocoa/40 backdrop-blur-sm"
              onClick={() => setMoreOpen(false)}
              aria-hidden="true"
            />
            <motion.div
              className="absolute bottom-0 inset-x-0 bg-porcelain rounded-t-[2rem] border-t border-sandBeige p-6 pb-10"
              style={{ backgroundColor: "#FFFCF8", paddingBottom: "calc(env(safe-area-inset-bottom) + 2.5rem)" }}
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", stiffness: 380, damping: 36 }}
            >
              <div className="mx-auto mb-5 w-12 h-1 rounded-full bg-sandBeige" aria-hidden="true" />
              <p className="font-serif text-2xl text-cocoa mb-4">{t("invPortal.shell.moreTitle")}</p>
              <ul className="flex flex-col gap-1">
                <li>
                  <NavLink to="/portal/investor/timeline" onClick={() => setMoreOpen(false)}
                    className="flex items-center gap-3 px-3 py-3 rounded-2xl text-cocoa hover:bg-ivory"
                    data-testid="inv-more-timeline"
                  >
                    <ClipboardList size={18} /> {t("invPortal.nav.timeline")}
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/portal/investor/security" onClick={() => setMoreOpen(false)}
                    className="flex items-center gap-3 px-3 py-3 rounded-2xl text-cocoa hover:bg-ivory"
                    data-testid="inv-more-security"
                  >
                    <Shield size={18} /> {t("invPortal.nav.security")}
                  </NavLink>
                </li>
                {SECONDARY.map(({ to, labelKey, icon: Icon, phaseKey }) => (
                  <li key={to}>
                    <NavLink to={to} onClick={() => setMoreOpen(false)}
                      className="flex items-center gap-3 px-3 py-3 rounded-2xl text-cocoa hover:bg-ivory"
                      data-testid={`inv-more-${to.split("/").pop()}`}
                    >
                      <Icon size={18} />
                      <span className="flex-1">{t(labelKey)}</span>
                      <span className="text-[10px] uppercase tracking-wide text-warmGrey">{t(`invPortal.phases.${phaseKey}`)}</span>
                    </NavLink>
                  </li>
                ))}
                <li className="mt-2 pt-2 border-t border-sandBeige">
                  <a href="mailto:hello@womenintech.life"
                    className="flex items-center gap-3 px-3 py-3 rounded-2xl text-cocoa hover:bg-ivory"
                    data-testid="inv-more-help"
                  >
                    <Mail size={18} /> {t("invPortal.shell.help")}
                  </a>
                </li>
                <li>
                  <button type="button"
                    onClick={() => { setMoreOpen(false); onLogout(); }}
                    className="w-full flex items-center gap-3 px-3 py-3 rounded-2xl text-cocoa hover:bg-ivory text-left"
                    data-testid="inv-more-logout"
                  >
                    <LogOut size={18} /> {t("invPortal.shell.logout")}
                  </button>
                </li>
              </ul>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
